import {
  gridPageCountSelector,
  gridPaginationModelSelector,
  gridPaginationRowCountSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from '@mui/x-data-grid';
import { useCallback, useMemo, type ReactElement } from 'react';
import { Box } from './Box';
import { TablePaginationBar } from './TablePaginationBar';
import { tablePaginationChromeSx } from './tablePaginationChrome';

const isPageSizeIncludedInPageSizeOptions = (
  pageSize: number,
  pageSizeOptions: ReadonlyArray<number | { value: number; label: string }>,
): boolean => {
  for (const option of pageSizeOptions) {
    if (typeof option === 'number') {
      if (option === pageSize) {
        return true;
      }
      continue;
    }

    if (option.value === pageSize) {
      return true;
    }
  }

  return false;
};

export const DataGridPaginationFooter = (): ReactElement => {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps() as ReturnType<typeof useGridRootProps> & {
    hidePaginationControls?: boolean;
  };
  const hidePaginationControls = rootProps.hidePaginationControls === true;
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const rowCount = useGridSelector(apiRef, gridPaginationRowCountSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const disabled = rowCount === -1 && rootProps.paginationMode === 'server' && rootProps.loading;
  const lastPage = useMemo(() => Math.max(0, pageCount - 1), [pageCount]);
  const computedPage = useMemo(() => {
    if (rowCount === -1) {
      return paginationModel.page;
    }

    return paginationModel.page <= lastPage ? paginationModel.page : lastPage;
  }, [lastPage, paginationModel.page, rowCount]);
  const handlePageChange = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement> | null, page: number): void => {
      apiRef.current.setPage(page);
    },
    [apiRef],
  );
  const handleRowsPerPageChange = useCallback(
    (nextRowsPerPage: number): void => {
      apiRef.current.setPageSize(nextRowsPerPage);
    },
    [apiRef],
  );

  const pageSizeOptions = isPageSizeIncludedInPageSizeOptions(
    paginationModel.pageSize,
    rootProps.pageSizeOptions,
  )
    ? rootProps.pageSizeOptions
    : [];
  const hideRowsPerPage = pageSizeOptions.length === 0;

  if (hidePaginationControls) {
    return <Box aria-hidden="true" sx={tablePaginationChromeSx} />;
  }

  return (
    <Box sx={tablePaginationChromeSx}>
      <TablePaginationBar
        count={rowCount}
        disabled={disabled}
        getItemAriaLabel={apiRef.current.getLocaleText('paginationItemAriaLabel')}
        labelDisplayedRows={(params) =>
          apiRef.current.getLocaleText('paginationDisplayedRows')({
            ...params,
            estimated: rootProps.estimatedRowCount,
          })
        }
        labelRowsPerPage={
          hideRowsPerPage ? null : apiRef.current.getLocaleText('paginationRowsPerPage')
        }
        onPageChange={handlePageChange}
        onRowsPerPageChange={hideRowsPerPage ? undefined : handleRowsPerPageChange}
        page={computedPage}
        rowsPerPage={paginationModel.pageSize}
        rowsPerPageOptions={hideRowsPerPage ? [] : pageSizeOptions}
      />
    </Box>
  );
};
